import {
  DownloadOutlined,
  SoundOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';

import { useLazyDownloadQuery } from '@app/api/ClientFiles';
import { AiGrade } from '@app/components/LeadCard/ui/MessageHistory/components/AiGrade';
import { LeadAudioPlayer } from '@app/components/LeadCard/ui/MessageHistory/components/LeadAudioPlayer';
import { LeadMessageTranscription } from '@app/components/LeadCard/ui/MessageHistory/components/LeadMessageTranscription';
import {
  LEAD_MSG_STATUS_DELETED,
  formatLeadMessageText,
  formatLeadMessageTimestamp,
  getLeadMessageStatusColor,
  getLeadMessageStatusIcon,
  isLeadAudioMessageType,
  parseLeadAudioDuration,
} from '@app/components/LeadCard/ui/MessageHistory/lib';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NFiles } from '@app/types';
import { buildAudioDownloadName } from '@app/utils';

import type { FC } from 'react';

import './LeadAudioMessage.scss';

const AUDIO_EXTENSIONS = new Set(['mp3', 'ogg', 'wav', 'amr', 'opus']);

const getAudioMimeByExt = (ext: string): string => {
  switch (ext) {
    case 'mp3':
      return 'audio/mpeg';
    case 'ogg':
    case 'opus':
      return 'audio/ogg';
    case 'wav':
      return 'audio/wav';
    case 'amr':
      return 'audio/amr';
    case 'mp4':
      return 'audio/mp4';
    default:
      return 'audio/mpeg';
  }
};

interface LeadAudioMessageProps {
  // null — аудио из файлового действия истории: у него нет сообщения, к
  // которому можно прокрутить.
  messageId: number | null;
  file: NFiles.ModelClientFile | null;
  messageType: string | null;
  // Расшифровка речи и оценка ИИ приходят на самом сообщении. У аудио из
  // файлового действия истории сообщения нет, поэтому поля необязательные.
  transcription?: string | null;
  grade?: number | null;
  aiRecommendations?: string | null;
  socialType: string | null;
  isIncoming: boolean;
  timestamp: string;
  wasEdited: boolean;
  status: string | null;
  isForwarded: boolean;
  replyMessage: string | null;
  replyMessageId: number | null;
  showStatus?: boolean;
}

export const LeadAudioMessage: FC<LeadAudioMessageProps> = ({
  messageId,
  file,
  messageType,
  transcription = null,
  grade = null,
  aiRecommendations = null,
  socialType,
  isIncoming,
  timestamp,
  wasEdited,
  status,
  isForwarded,
  replyMessage,
  replyMessageId,
  showStatus = true,
}) => {
  const { params } = useUserPrint();
  const { showNotification } = useNotification();
  const [downloadFile] = useLazyDownloadQuery();

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadAnchorRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const ext = (file?.Extension ?? '').trim().toLowerCase();
  const mime = getAudioMimeByExt(ext);
  // Instagram voice notes arrive as an MP4 audio container, so MP4 is playable
  // only when the message is semantically audio (VOICE/AUDIO), never for plain
  // MP4 video attachments.
  const isMp4AudioMessage =
    isLeadAudioMessageType(messageType) && ext === 'mp4';
  const canPlay = !!file && (AUDIO_EXTENSIONS.has(ext) || isMp4AudioMessage);

  const isVoiceMessage =
    messageType === 'VOICE' ||
    ext === 'opus' ||
    ext === 'ogg' ||
    file?.Description === 'Аудио сообщение';

  const title = isVoiceMessage
    ? 'Аудио сообщение'
    : file?.FileName ?? 'Аудио файл';

  const isDeleted = status === LEAD_MSG_STATUS_DELETED;
  const showStatusIcon = !isIncoming || isDeleted;
  const statusIcon = showStatusIcon ? getLeadMessageStatusIcon(status) : null;
  const statusColor = getLeadMessageStatusColor(status);
  const formattedTimestamp = formatLeadMessageTimestamp(timestamp);

  const sanitizedReplyHtml = replyMessage
    ? DOMPurify.sanitize(formatLeadMessageText(replyMessage, socialType))
    : null;

  const loadAudioUrl = async (): Promise<string | null> => {
    if (audioUrl) return audioUrl;
    if (!file) return null;

    try {
      const buffer = await downloadFile({
        clientId: file.ClientId,
        fileId: file.Id,
        ActiveBranchId: params.ActiveBranchId,
      }).unwrap();

      const blob = new Blob([buffer], { type: mime });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      return url;
    } catch (error) {
      showNotification(error);
      return null;
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      const url = await loadAudioUrl();
      if (!url || !downloadAnchorRef.current) return;

      downloadAnchorRef.current.href = url;
      downloadAnchorRef.current.download = buildAudioDownloadName(
        file?.FileName,
        file?.Extension,
        `audio-${file?.ClientId ?? 0}-${file?.Id ?? 0}`
      );
      downloadAnchorRef.current.click();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className={cn('LeadAudioMessage', {
        'LeadAudioMessage--out': !isIncoming,
        'LeadAudioMessage--deleted': isDeleted,
      })}
    >
      {isForwarded && (
        <div className="LeadAudioMessage__forwarded">
          <ShareAltOutlined className="LeadAudioMessage__forwarded-icon" />
          Пересланное сообщение
        </div>
      )}
      {sanitizedReplyHtml && (
        <div
          className="LeadAudioMessage__reply"
          data-reply-target-id={replyMessageId ?? undefined}
        >
          <div className="LeadAudioMessage__reply-indicator" />
          <div
            className="LeadAudioMessage__reply-text"
            dangerouslySetInnerHTML={{ __html: sanitizedReplyHtml }}
          />
        </div>
      )}
      <div className="LeadAudioMessage__title">
        <SoundOutlined className="LeadAudioMessage__title-icon" />
        <span className="LeadAudioMessage__title-text">{title}</span>
        <AiGrade
          grade={grade}
          aiRecommendations={aiRecommendations}
          subject="сообщения"
        />
      </div>
      {!file && (
        <div className="LeadAudioMessage__unavailable">Аудио недоступно</div>
      )}
      {canPlay && file && (
        <LeadAudioPlayer
          fileId={file.Id}
          messageId={messageId}
          title={title}
          isIncoming={isIncoming}
          durationSec={parseLeadAudioDuration(file.Duration)}
          onRequestUrl={loadAudioUrl}
          actions={
            <Button
              type="text"
              size="small"
              className="LeadAudioMessage__download-btn"
              icon={<DownloadOutlined />}
              loading={isDownloading}
              onClick={handleDownload}
            />
          }
        />
      )}
      <LeadMessageTranscription text={transcription} />
      <a
        ref={downloadAnchorRef}
        className="LeadAudioMessage__download-anchor"
      />
      {showStatus && (
        <div className="LeadAudioMessage__status">
          <span className="LeadAudioMessage__timestamp">
            {wasEdited && 'изменено '}
            {formattedTimestamp}
          </span>
          {statusIcon && (
            <span
              className="LeadAudioMessage__status-icon"
              style={{ color: statusColor }}
            >
              {statusIcon}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
